import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Box,
} from "@mui/material";
import PaperDraggable from "../../../../../../../../../../components/PaperDraggable";
import SelectFormaRecebimento, {
  TObjValueFormRecebimento,
} from "../../../../../../../../../../components/SelectsSimpleApiQuery/SelectFormaRecebimento";
import { useFormContext } from "react-hook-form";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  formatarDecimalNumber,
  formatarInputValorMonetárioSemPrefixo,
  formatarMonetárioParaNúmero,
} from "../../../../../../../../../../helpers/masks";
import { Moment } from "moment";
import { TBancos } from "../../../../../../../../../../types/api/financeiro/bancos";
import { ContaContabil } from "../../../../../../../../../../types/api/geral/pesquisar-plano-contas";
import { enqueueSnackbar } from "notistack";
import DialogSimularValor from "./DialogSimularValor";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

export default function DialogCadastrarRecb({
  open,
  value,
  handleClose,
}: DialogCadastrarRecbProps) {
  const { setValue, getValues } = useFormContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [hasFocusedButton, setHasFocusedButton] = useState(false);
  const [openSimular, setOpenSimular] = useState(false);

  const editModalMode = useMemo(() => {
    const formValues = getValues("finalizacao.recebimentos");
    return value !== null || (value === null && formValues.length > 0);
  }, [value, getValues]);

  const valorRestante = useMemo(() => {
    const totalAtt = getValues("total_atualizado");

    if (editModalMode) {
      return totalAtt;
    } else {
      const valoresRec = getValues("finalizacao.recebimentos");
      let totalValores = 0;
      if (valoresRec.length > 0) {
        totalValores = valoresRec.reduce(
          (acumulador: number, item: any) =>
            acumulador + formatarMonetárioParaNúmero(item.valor),
          0
        );
      }
      return totalAtt - totalValores;
    }
  }, [getValues, editModalMode]);

  // Estado para o formulário atual sendo editado
  const [currentForm, setCurrentForm] = useState<IInternForm>({
    formaPagamento: null,
    valor: formatarDecimalNumber(valorRestante),
  });

  // Estado para a lista de recebimentos
  const [recebimentosList, setRecebimentosList] = useState<IInternForm[]>([]);

  // Estado para controlar se está editando um item da lista
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  //funções manipuladoras
  const handleChangeCurrentForm = (prop: keyof IInternForm, value: any) => {
    setCurrentForm((prevState) => ({
      ...prevState,
      [prop]: value,
    }));
  };

  const handleAddRecebimento = () => {
    if (!currentForm.formaPagamento) {
      enqueueSnackbar("Selecione uma forma de pagamento", { variant: "warning" });
      return;
    }

    if (formatarMonetárioParaNúmero(currentForm.valor) <= 0) {
      enqueueSnackbar("Informe um valor válido", { variant: "warning" });
      return;
    }

    // Verificar se precisa de tratativa baseado nas propriedades da forma de pagamento
    const precisaTratativa = Boolean(
      currentForm.formaPagamento?.requerNsu ||
      currentForm.formaPagamento?.requerChequeInfo ||
      currentForm.formaPagamento?.requerModalPix
    );

    // Criar objeto com o parâmetro precisa_tratativa
    const itemCompleto = {
      ...currentForm,
      precisa_tratativa: precisaTratativa
    };

    if (editingIndex !== null) {
      // Editando item existente
      const newList = [...recebimentosList];
      newList[editingIndex] = itemCompleto;
      setRecebimentosList(newList);
      setEditingIndex(null);
    } else {
      // Adicionando novo item
      setRecebimentosList([...recebimentosList, itemCompleto]);
    }

    // Limpar formulário atual e atualizar valor para o restante
    const novoValorRestante = valorRestante - totalAdicionado - formatarMonetárioParaNúmero(currentForm.valor);
    setCurrentForm({
      formaPagamento: null,
      valor: formatarDecimalNumber(novoValorRestante > 0 ? novoValorRestante : 0),
    });
  };

  const handleEditRecebimento = (index: number) => {
    const item = recebimentosList[index];
    setCurrentForm({ ...item });
    setEditingIndex(index);
  };

  const handleRemoveRecebimento = (index: number) => {
    const newList = recebimentosList.filter((_, i) => i !== index);
    setRecebimentosList(newList);
  };

  const handleSubmitForm = () => {
    if (recebimentosList.length === 0) {
      enqueueSnackbar("Adicione pelo menos uma forma de recebimento", { variant: "warning" });
      return;
    }

    if (editModalMode) {
      // Modo edição - substituir toda a lista
      setValue("finalizacao.recebimentos", recebimentosList);
    } else {
      // Modo adição - adicionar todos os itens
      const arrRecb = getValues("finalizacao.recebimentos");
      arrRecb.push(...recebimentosList);
      setValue("finalizacao.recebimentos", arrRecb);
    }

    handleClose();
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setCurrentForm({
      formaPagamento: null,
      valor: formatarDecimalNumber(valorRestante),
    });
  };

  //valores importantes
  const totalAdicionado = useMemo(() => {
    return recebimentosList.reduce((total, item) => {
      return total + formatarMonetárioParaNúmero(item.valor);
    }, 0);
  }, [recebimentosList]);

  const btnConfirmDisabled = useMemo(() => {
    return recebimentosList.length === 0 || totalAdicionado === 0;
  }, [recebimentosList, totalAdicionado]);


  useEffect(() => {
    if (editModalMode && open) {
      const formValues = getValues("finalizacao.recebimentos");
      setRecebimentosList([...formValues]);
    }
  }, [editModalMode, open, getValues]);

  useEffect(() => {
    if (!hasFocusedButton && value !== null) {
      if (buttonRef.current) {
        buttonRef.current.focus();
        setHasFocusedButton(true);
      }
    }
  }, [hasFocusedButton, value]);

  return (
    <>
      <Dialog
        open={open}
        PaperComponent={PaperDraggable}
        aria-labelledby="draggable-dialog-title"
        scroll="body"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
          Cadastrar formas de recebimento
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} my={1}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SelectFormaRecebimento
                value={currentForm.formaPagamento}
                onChange={(value) => {
                  handleChangeCurrentForm("formaPagamento", value);
                }}
                loja_id={getValues("finalizacao.loja")?.id}
                flg_usa_receber={true}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction={'column'} spacing={1}>
                <TextField
                  fullWidth
                  label="Valor recebido"
                  type="text"
                  value={currentForm.valor}
                  onChange={(e) => {
                    const formattedValue = formatarInputValorMonetárioSemPrefixo(
                      e.target.value
                    );
                    handleChangeCurrentForm("valor", formattedValue);
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography color={"#000"}>R$</Typography>
                        </InputAdornment>
                      )
                    }
                  }}
                />
                <Stack direction="row" justifyContent="flex-end" alignItems="flex-start">
                  <Link variant="subtitle2" onClick={() => { setOpenSimular(true) }} sx={{ cursor: 'pointer' }}>
                    Simular valor
                  </Link>
                </Stack>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={handleAddRecebimento}
                >
                  {editingIndex !== null ? "Atualizar" : "Adicionar"}
                </Button>
                {editingIndex !== null && (
                  <Button
                    variant="text"
                    onClick={handleCancelEdit}
                  >
                    Cancelar
                  </Button>
                )}
              </Stack>
            </Grid>

            {/* Lista de recebimentos adicionados */}
            {recebimentosList.length > 0 && (
              <>
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ mt: 2 }} />

                  <List>
                    {recebimentosList.map((item, index) => (
                      <ListItem key={index} divider>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="subtitle2">
                                {item.formaPagamento?.label}
                              </Typography>
                              <Chip
                                label={`R$ ${item.valor}`}
                                color="primary"
                                size="small"
                              />
                            </Stack>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleEditRecebimento(index)}
                            >
                              <EditIcon fontSize="inherit" />
                            </IconButton>

                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleRemoveRecebimento(index)}
                            >
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </Stack>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: totalAdicionado > valorRestante ? "success.main" : "error.main"
                      }}
                    >
                      Total adicionado: <strong>R$ {formatarDecimalNumber(totalAdicionado)}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Valor restante: <strong>R$ {formatarDecimalNumber(valorRestante - totalAdicionado > 0 ? valorRestante - totalAdicionado : 0)}</strong>
                    </Typography>
                    {
                      valorRestante - totalAdicionado < 0 &&
                      <Typography variant="body2" color="warning.main">
                        Valor excedente: <strong>R$ {formatarDecimalNumber(Math.abs(valorRestante - totalAdicionado))}</strong>
                      </Typography>
                    }
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            ref={buttonRef}
            autoFocus
            variant="contained"
            onClick={handleSubmitForm}
            disabled={btnConfirmDisabled}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      {
        openSimular &&
        <DialogSimularValor
          open={openSimular}
          handleClose={() => setOpenSimular(false)}
          handleSimular={(valorSimulado) => {
            handleChangeCurrentForm("valor", formatarDecimalNumber(valorSimulado));
            setOpenSimular(false)
          }}
        />
      }
    </>
  );
}

interface DialogCadastrarRecbProps {
  open: boolean;
  value: number | null;
  handleClose: () => void;
}

export interface IInternForm {
  formaPagamento: TObjValueFormRecebimento | null;
  valor: string;
  precisa_tratativa?: boolean;

  //cartão
  cartao_nsu?: string;
  cartao_autorizacao?: string;
  cartao_parcelas?: number;

  //cheque
  cheque_banco?: TBancos | null;
  cheque_agencia?: string;
  cheque_agencia_digito?: string;
  cheque_conta?: string;
  cheque_conta_digito?: string;
  cheque_documento?: string;
  cheque_num?: string;
  cheque_seq?: string;
  cheque_data_vencimento?: Moment | null;
  cheque_nome_emissor?: string;
  cheque_praca?: string;
  cheque_conta_contabil?: ContaContabil | null;

  //pix
  pix_chave?: string;
  pix_pago?: boolean;
}
